import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Search, Loader2, ChevronDown, ChevronRight, AlertTriangle, Save, HelpCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { patchLocalItemPrices } from '@/offline/repositories';
import { useBulkItemSelection, type BulkSelectableRow } from '@/hooks/useBulkItemSelection';
import type { POSItem } from '@/types';

interface Props {
  onClose: () => void;
  onPrintLabels?: (rows: BulkSelectableRow[]) => void;
}

type PriceField = 'sale' | 'wholesale' | 'reseller' | 'minimum';
type PriceEdits = Record<string, Partial<Record<PriceField, number>>>;

const PRICE_FIELDS: PriceField[] = ['sale', 'wholesale', 'reseller', 'minimum'];
const EDIT_GRID_COLS = 'grid-cols-[2.5rem_minmax(0,1.4fr)_repeat(4,minmax(5rem,1fr))]';

const BULK_RULE_HELP_KEYS = [
  { labelKey: 'items.rulePercent', helpKey: 'items.rulePercentHelp' },
  { labelKey: 'items.ruleAmount', helpKey: 'items.ruleAmountHelp' },
  { labelKey: 'items.ruleSet', helpKey: 'items.ruleSetHelp' },
  { labelKey: 'items.ruleMargin', helpKey: 'items.ruleMarginHelp' },
] as const;

function decimalPlaces(step: number): number {
  if (step >= 1) return 0;
  const s = step.toString();
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

function roundPrice(value: number, step: number): number {
  let rounded = value;
  if (step > 0) {
    rounded = Math.round(value / step) * step;
  }
  const dp = step > 0 ? decimalPlaces(step) : 2;
  return Number(rounded.toFixed(dp));
}

function getOldPrice(row: BulkSelectableRow, field: PriceField): number {
  if (field === 'sale') return row.salePrice;
  if (field === 'wholesale') return row.wholesalePrice;
  if (field === 'reseller') return row.resellerPrice;
  return row.minimumPrice;
}

function getNewPrice(row: BulkSelectableRow, edits: PriceEdits, field: PriceField): number {
  const edited = edits[row.key]?.[field];
  if (edited != null && !Number.isNaN(edited)) return edited;
  return getOldPrice(row, field);
}

function rowHasChanges(row: BulkSelectableRow, edits: PriceEdits): boolean {
  const e = edits[row.key];
  if (!e) return false;
  return PRICE_FIELDS.some((f) => e[f] != null && e[f] !== getOldPrice(row, f));
}

export default function BulkPriceUpdateModal({ onClose, onPrintLabels }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sel = useBulkItemSelection('Product');
  const [edits, setEdits] = useState<PriceEdits>({});
  const [showReview, setShowReview] = useState(false);
  const [ruleType, setRuleType] = useState<'percent' | 'amount' | 'set' | 'margin'>('percent');
  const [ruleValue, setRuleValue] = useState('');
  const [ruleTarget, setRuleTarget] = useState<'sale' | 'wholesale' | 'reseller' | 'minimum' | 'all'>('sale');
  const [roundToStep, setRoundToStep] = useState('0.05');
  const [ruleApplyKeys, setRuleApplyKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setRuleApplyKeys(new Set(sel.selectedRows.map((row) => row.key)));
  }, [sel.selectedRows]);

  const changedRows = useMemo(
    () => sel.selectedRows.filter((row) => rowHasChanges(row, edits)),
    [sel.selectedRows, edits],
  );

  const applyRuleToField = (base: number, cost: number): number => {
    const val = parseFloat(ruleValue);
    if (Number.isNaN(val)) return base;
    let next = base;
    if (ruleType === 'set') next = val;
    else if (ruleType === 'percent') next = base * (1 + val / 100);
    else if (ruleType === 'amount') next = base + val;
    else if (ruleType === 'margin') next = cost > 0 ? cost * (1 + val / 100) : base;
    const step = parseFloat(roundToStep) || 0;
    return Math.max(0, roundPrice(next, step));
  };

  const applyRule = () => {
    const val = parseFloat(ruleValue);
    if (Number.isNaN(val)) {
      toast.error(t('items.invalidRuleValue'));
      return;
    }
    const targets: PriceField[] = ruleTarget === 'all' ? PRICE_FIELDS : [ruleTarget];
    setEdits((prev) => {
      const next = { ...prev };
      for (const row of sel.selectedRows) {
        if (!ruleApplyKeys.has(row.key)) continue;
        const rowEdits = { ...next[row.key] };
        for (const field of targets) {
          const base = getNewPrice(row, prev, field);
          rowEdits[field] = applyRuleToField(base, row.costPrice);
        }
        next[row.key] = rowEdits;
      }
      return next;
    });
    toast.success(t('items.ruleApplied'));
  };

  const setFieldEdit = (row: BulkSelectableRow, field: PriceField, raw: string) => {
    const v = parseFloat(raw);
    const step = parseFloat(roundToStep) || 0;
    setEdits((prev) => ({
      ...prev,
      [row.key]: { ...prev[row.key], [field]: Number.isNaN(v) ? undefined : roundPrice(v, step) },
    }));
  };

  const displayPrice = (row: BulkSelectableRow, field: PriceField) => {
    const step = parseFloat(roundToStep) || 0;
    return roundPrice(getNewPrice(row, edits, field), step);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = changedRows.map((row) => {
        const step = parseFloat(roundToStep) || 0;
        const sale = roundPrice(getNewPrice(row, edits, 'sale'), step);
        const wholesale = roundPrice(getNewPrice(row, edits, 'wholesale'), step);
        const reseller = roundPrice(getNewPrice(row, edits, 'reseller'), step);
        const minimum = roundPrice(getNewPrice(row, edits, 'minimum'), step);
        return {
          name: row.item.name,
          ...(row.variant ? { variant: row.variant.name } : {}),
          ...(sale !== row.salePrice ? { sale_price: sale } : {}),
          ...(wholesale !== row.wholesalePrice ? { wholesale_price: wholesale } : {}),
          ...(reseller !== row.resellerPrice ? { reseller_price: reseller } : {}),
          ...(minimum !== row.minimumPrice ? { minimum_price: minimum } : {}),
        };
      });
      const res = await posApi.bulkUpdateItemPrices(payload);
      return res.data.message as { updated_count: number; errors: { message: string }[] };
    },
    onSuccess: async (data) => {
      if (data.errors?.length) {
        toast.error(data.errors[0]?.message || t('items.priceUpdatePartialFail'));
      } else {
        toast.success(t('items.priceUpdateSuccess', { count: data.updated_count }));
      }
      await patchLocalItemPrices(
        changedRows.map((row) => {
          const e = edits[row.key] || {};
          return {
            name: row.item.name,
            variant: row.variant?.name,
            sale_price: e.sale,
            wholesale_price: e.wholesale,
            reseller_price: e.reseller,
            minimum_price: e.minimum,
          };
        }),
      );
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['bulkItemSelection'] });
      void qc.invalidateQueries({ queryKey: ['bulkPrintItems'] });
      if (onPrintLabels && changedRows.length > 0) {
        onPrintLabels(changedRows);
      }
      onClose();
    },
    onError: () => toast.error(t('items.priceUpdateFail')),
  });

  const handleSave = () => {
    if (changedRows.length === 0) {
      toast.error(t('items.noPriceChanges'));
      return;
    }
    const bigJump = changedRows.some((row) => {
      const oldP = row.salePrice || 1;
      const newP = getNewPrice(row, edits, 'sale');
      return Math.abs(newP - oldP) / oldP > 0.2;
    });
    if (bigJump && !window.confirm(t('items.confirmLargePriceChange'))) return;
    saveMutation.mutate();
  };

  const priceFieldLabel = (field: PriceField) => {
    if (field === 'sale') return t('items.retailPrice');
    if (field === 'wholesale') return t('items.wholesalePrice');
    if (field === 'reseller') return t('items.resellerPrice');
    return t('items.minimumPrice');
  };

  const toggleRuleApplyKey = (key: string) => {
    setRuleApplyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allRuleApplyChecked = sel.selectedRows.length > 0 && sel.selectedRows.every((row) => ruleApplyKeys.has(row.key));
  const someRuleApplyChecked = sel.selectedRows.some((row) => ruleApplyKeys.has(row.key));

  const toggleAllRuleApply = () => {
    if (allRuleApplyChecked) {
      setRuleApplyKeys(new Set());
    } else {
      setRuleApplyKeys(new Set(sel.selectedRows.map((row) => row.key)));
    }
  };

  const renderSelectRow = (row: BulkSelectableRow) => {
    const checked = sel.selectedKeys.has(row.key);
    return (
      <label
        key={row.key}
        className={`w-full flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${checked ? 'bg-primary-50/60' : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => sel.toggleKey(row.key)}
          className="w-4 h-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{row.label}</span>
        <span className="text-sm text-gray-600 shrink-0">{formatMoney(row.salePrice)}</span>
      </label>
    );
  };

  const renderEditRow = (row: BulkSelectableRow) => (
    <div key={row.key} className={`grid ${EDIT_GRID_COLS} gap-2 px-3 py-2 border-b border-gray-50 items-center`}>
      <input
        type="checkbox"
        checked={ruleApplyKeys.has(row.key)}
        onChange={() => toggleRuleApplyKey(row.key)}
        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        aria-label={row.label}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{row.label}</p>
        <p className="text-xs text-gray-500 truncate">{row.categoryName}</p>
      </div>
      {PRICE_FIELDS.map((field) => {
        const oldVal = getOldPrice(row, field);
        const newVal = displayPrice(row, field);
        const changed = newVal !== roundPrice(oldVal, parseFloat(roundToStep) || 0);
        return (
          <input
            key={field}
            type="number"
            min={0}
            step={roundToStep === '0' ? '0.01' : roundToStep}
            title={priceFieldLabel(field)}
            className={`input-field w-full text-sm py-1.5 min-h-[40px] ${changed ? 'border-amber-400 bg-amber-50' : ''}`}
            value={newVal}
            onChange={(e) => setFieldEdit(row, field, e.target.value)}
          />
        );
      })}
    </div>
  );

  const renderItemRow = (item: POSItem) => {
    const expanded = !!sel.expandedItems[item.name];
    const variants = sel.loadedVariants[item.name];
    const loading = !!sel.loadingVariants[item.name];

    if (item.has_variants) {
      return (
        <div key={item.name} className="border-b border-gray-100">
          <button
            type="button"
            onClick={() => void sel.toggleExpand(item)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="text-sm font-medium text-gray-900 flex-1 truncate">{item.item_name}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </button>
          {expanded && variants?.map((v) => {
            const key = `${item.name}__${v.name}`;
            const row = sel.flatRows.find((r) => r.key === key);
            if (!row) return null;
            return renderSelectRow(row);
          })}
        </div>
      );
    }

    const row = sel.flatRows.find((r) => r.key === item.name);
    if (!row) return null;
    return renderSelectRow(row);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('items.updatePrices')}</h2>
            <p className="text-sm text-gray-500">{t('items.updatePricesHelp')}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-80 border-r border-gray-100 flex flex-col shrink-0">
            <div className="p-3 space-y-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  className="input-field pl-9 text-sm"
                  placeholder={t('common.search')}
                  value={sel.search}
                  onChange={(e) => sel.setSearch(e.target.value)}
                />
              </div>
              <select className="input-field text-sm" value={sel.category} onChange={(e) => sel.setCategory(e.target.value)}>
                <option value="All">{t('items.allCategories')}</option>
                {sel.categories.map((c) => (
                  <option key={c.name} value={c.name}>{c.category_name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button type="button" onClick={() => void sel.selectAllVisible()} className="btn-secondary text-xs flex-1 py-2">
                  {t('items.selectAll')}
                </button>
                <button type="button" onClick={sel.clearSelection} className="btn-secondary text-xs flex-1 py-2">
                  {t('items.clearSelection')}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('items.selectedCount', { count: sel.selectedKeys.size })}</p>
            </div>
            <div className="flex-1 overflow-auto">
              {sel.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : (
                sel.searchResults.map((item) => renderItemRow(item))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0">
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex flex-wrap items-end gap-2">
              <div>
                <div className="flex items-center gap-1">
                  <label className="text-xs font-medium text-gray-600">{t('items.bulkRule')}</label>
                  <div className="group relative">
                    <HelpCircle
                      className="w-3.5 h-3.5 text-gray-400 cursor-help"
                      aria-label={t('items.bulkRuleHelpTitle')}
                    />
                    <div className="absolute left-0 top-full mt-1.5 hidden group-hover:block w-80 max-w-[min(20rem,calc(100vw-2rem))] bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 z-30 pointer-events-none">
                      <p className="font-semibold text-gray-100 mb-2">{t('items.bulkRuleHelpTitle')}</p>
                      <ul className="space-y-1.5">
                        {BULK_RULE_HELP_KEYS.map(({ labelKey, helpKey }) => (
                          <li key={helpKey} className="leading-snug">
                            <span className="font-medium text-gray-200">{t(labelKey)}:</span>{' '}
                            <span className="text-gray-300">{t(helpKey)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="absolute left-3 -top-1.5 w-3 h-3 bg-gray-900 rotate-45" />
                    </div>
                  </div>
                </div>
                <select className="input-field text-sm mt-1" value={ruleType} onChange={(e) => setRuleType(e.target.value as typeof ruleType)}>
                  <option value="percent">{t('items.rulePercent')}</option>
                  <option value="amount">{t('items.ruleAmount')}</option>
                  <option value="set">{t('items.ruleSet')}</option>
                  <option value="margin">{t('items.ruleMargin')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.ruleValue')}</label>
                <input className="input-field text-sm mt-1 w-28" value={ruleValue} onChange={(e) => setRuleValue(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.applyToPrice')}</label>
                <select className="input-field text-sm mt-1 w-36" value={ruleTarget} onChange={(e) => setRuleTarget(e.target.value as typeof ruleTarget)}>
                  <option value="sale">{t('items.retailPrice')}</option>
                  <option value="wholesale">{t('items.wholesalePrice')}</option>
                  <option value="reseller">{t('items.resellerPrice')}</option>
                  <option value="minimum">{t('items.minimumPrice')}</option>
                  <option value="all">{t('items.allPrices')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.roundTo')}</label>
                <select className="input-field text-sm mt-1 w-24" value={roundToStep} onChange={(e) => setRoundToStep(e.target.value)}>
                  <option value="0">{t('items.noRounding')}</option>
                  <option value="0.05">0.05</option>
                  <option value="0.10">0.10</option>
                  <option value="0.50">0.50</option>
                  <option value="1">1.00</option>
                </select>
              </div>
              <button type="button" onClick={applyRule} disabled={ruleApplyKeys.size === 0} className="btn-secondary text-sm py-2">
                {t('items.applyRule')}
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {sel.selectedRows.length === 0 ? (
                <p className="text-center text-gray-400 py-12 text-sm">{t('items.selectProductsToEdit')}</p>
              ) : (
                <>
                  <div className={`grid ${EDIT_GRID_COLS} gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10 items-center`}>
                    <input
                      type="checkbox"
                      checked={allRuleApplyChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someRuleApplyChecked && !allRuleApplyChecked;
                      }}
                      onChange={toggleAllRuleApply}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('items.selectAll')}
                    />
                    <span className="text-xs font-semibold text-gray-600 uppercase">{t('items.product')}</span>
                    {PRICE_FIELDS.map((field) => (
                      <span key={field} className="text-xs font-semibold text-gray-600 uppercase truncate">
                        {priceFieldLabel(field)}
                      </span>
                    ))}
                  </div>
                  {sel.selectedRows.map((row) => renderEditRow(row))}
                </>
              )}
            </div>

            {showReview && changedRows.length > 0 && (
              <div className="border-t border-amber-200 bg-amber-50 p-3 max-h-32 overflow-auto text-xs">
                {changedRows.map((row) => (
                  <div key={row.key} className="py-0.5">
                    <span className="font-medium truncate block">{row.label}</span>
                    {PRICE_FIELDS.filter((f) => {
                      const e = edits[row.key];
                      return e?.[f] != null && e[f] !== getOldPrice(row, f);
                    }).map((f) => (
                      <div key={f} className="flex justify-between gap-2 pl-2 text-gray-600">
                        <span>{priceFieldLabel(f)}</span>
                        <span>{formatMoney(getOldPrice(row, f))} → {formatMoney(getNewPrice(row, edits, f))}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3">
              <button type="button" onClick={() => setShowReview((v) => !v)} className="btn-secondary text-sm flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {t('items.reviewChanges')} ({changedRows.length})
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveMutation.isPending || changedRows.length === 0}
                className="btn-primary flex items-center gap-2"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('items.savePriceChanges', { count: changedRows.length })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
